import React, { useEffect, useState } from 'react';
import type { User } from '@supabase/supabase-js';
import { Loader2, KanbanSquare, MapPin, Phone, Mail, Clock } from 'lucide-react';
import {
  DndContext,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
  DragOverlay,
  useDroppable,
  useDraggable,
} from '@dnd-kit/core';

import { PIPELINE_STAGES, PIPELINE_LABELS, PIPELINE_COLORS, type PipelineStage } from '../constants';
import { listHomeowners, setPipelineStage, type HomeownerWithDetails } from '../services/homeowners';
import HomeownerDrawer from './HomeownerDrawer';
import { useToast } from './Toast';

interface PipelineScreenProps {
  user: User;
}

export default function PipelineScreen({ user }: PipelineScreenProps) {
  const [records, setRecords] = useState<HomeownerWithDetails[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [drawerId, setDrawerId] = useState<string | null>(null);
  const toast = useToast();

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  const refresh = async () => {
    setLoading(true);
    try {
      setRecords(await listHomeowners(user.id));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
  }, [user.id]);

  const handleDragEnd = async (e: DragEndEvent) => {
    setActiveId(null);
    if (!e.over) return;
    const recordId = String(e.active.id);
    const newStage = String(e.over.id) as PipelineStage;
    const record = records.find((r) => r.id === recordId);
    if (!record || record.pipeline_stage === newStage) return;

    setRecords((curr) =>
      curr.map((r) => (r.id === recordId ? { ...r, pipeline_stage: newStage } : r))
    );
    try {
      await setPipelineStage(recordId, newStage);
    } catch (err: any) {
      toast.error(err?.message || 'Failed to update stage');
      refresh();
    }
  };

  const grouped = PIPELINE_STAGES.reduce(
    (acc, stage) => {
      acc[stage] = records.filter((r) => r.pipeline_stage === stage);
      return acc;
    },
    {} as Record<PipelineStage, HomeownerWithDetails[]>
  );

  return (
    <div className="flex-1 min-h-0 flex flex-col">
      <div className="px-6 py-4 border-b border-slate-200 bg-white">
        <h1 className="text-2xl font-semibold text-slate-900 flex items-center gap-2">
          <KanbanSquare size={20} className="text-emerald-600" /> Pipeline
        </h1>
        <p className="text-sm text-slate-500 mt-0.5">
          Drag cards between columns to move leads through your funnel.
        </p>
      </div>

      {loading ? (
        <div className="flex-1 flex items-center justify-center">
          <Loader2 className="animate-spin text-slate-400" size={28} />
        </div>
      ) : (
        <DndContext
          sensors={sensors}
          onDragStart={(e) => setActiveId(String(e.active.id))}
          onDragCancel={() => setActiveId(null)}
          onDragEnd={handleDragEnd}
        >
          <div className="flex-1 min-h-0 overflow-x-auto p-6">
            <div className="flex gap-4 h-full min-w-max">
              {PIPELINE_STAGES.map((stage) => (
                <Column
                  key={stage}
                  stage={stage}
                  cards={grouped[stage]}
                  onOpen={(id) => setDrawerId(id)}
                />
              ))}
            </div>
          </div>

          <DragOverlay>
            {activeId ? (
              <Card record={records.find((r) => r.id === activeId)!} dragging onOpen={() => {}} />
            ) : null}
          </DragOverlay>
        </DndContext>
      )}

      {drawerId && (
        <HomeownerDrawer
          recordId={drawerId}
          agentId={user.id}
          onClose={() => setDrawerId(null)}
          onChanged={refresh}
        />
      )}
    </div>
  );
}

function Column({
  stage,
  cards,
  onOpen,
}: {
  stage: PipelineStage;
  cards: HomeownerWithDetails[];
  onOpen: (id: string) => void;
}) {
  const { isOver, setNodeRef } = useDroppable({ id: stage });
  return (
    <div
      ref={setNodeRef}
      className={`w-72 shrink-0 flex flex-col bg-slate-100 rounded-xl border ${
        isOver ? 'border-emerald-400 bg-emerald-50/60' : 'border-transparent'
      }`}
    >
      <div className="px-3 py-2.5 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className={`px-2 py-0.5 rounded text-xs font-medium ${PIPELINE_COLORS[stage]}`}>
            {PIPELINE_LABELS[stage]}
          </span>
        </div>
        <span className="text-xs text-slate-500 tabular-nums">{cards.length}</span>
      </div>
      <div className="flex-1 px-2 pb-2 overflow-y-auto space-y-2 min-h-[200px]">
        {cards.map((c) => (
          <Card key={c.id} record={c} onOpen={onOpen} />
        ))}
      </div>
    </div>
  );
}

function Card({
  record,
  onOpen,
  dragging,
}: {
  record: HomeownerWithDetails;
  onOpen: (id: string) => void;
  dragging?: boolean;
}) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({ id: record.id });
  const days = Math.floor(
    (Date.now() - new Date(record.stage_changed_at).getTime()) / (1000 * 60 * 60 * 24)
  );
  return (
    <div
      ref={setNodeRef}
      {...attributes}
      {...listeners}
      onClick={() => !isDragging && onOpen(record.id)}
      className={`group p-3 bg-white rounded-lg shadow-sm border border-slate-200 cursor-grab active:cursor-grabbing hover:border-emerald-300 hover:shadow transition-all ${
        dragging || isDragging ? 'opacity-50' : ''
      }`}
    >
      <div className="flex items-start gap-2 mb-2">
        <MapPin size={14} className="text-slate-400 shrink-0 mt-0.5" />
        <div className="text-sm font-medium text-slate-900 leading-snug truncate">
          {record.property?.address_line_1 || record.uprn}
        </div>
      </div>
      {record.contact_name && (
        <div className="text-xs text-slate-500 truncate">{record.contact_name}</div>
      )}
      <div className="flex items-center gap-3 mt-2 text-xs text-slate-500">
        {record.contact_phone && <Phone size={11} />}
        {record.contact_email && <Mail size={11} />}
        <span className="ml-auto flex items-center gap-1">
          <Clock size={11} /> {days}d
        </span>
      </div>
    </div>
  );
}
